<template>
    <TransitionRoot as="template" :show="open">
        <Dialog as="div" static class="fixed inset-0 z-50 overflow-y-auto" @close="$emit('cancel')" :open="open">
            <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0 ">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
                </TransitionChild>

                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">

                    <div class="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
                        <div class="sm:flex sm:items-start">

                            <div class="w-full mt-3 text-center sm:mt-0 sm:ml-4 sm:mr-4 sm:text-left">
                                <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">
                                    {{ $t("device.action.applyConfigTemplate") }}
                                </DialogTitle>
                                <div class="w-full mt-8">

                                    <Combobox as="div" v-model="selectedTemplate" class="w-full" :disabled="templatesLoading">
                                        <ComboboxLabel class="block text-sm font-medium leading-6 text-gray-900">
                                            {{ $t("configTemplates.listPageTitleEdit") }}
                                            <span v-if="templatesLoading" class="ml-2 text-sm text-gray-500">{{ $t("common.loading") }}...</span>
                                        </ComboboxLabel>
                                        <div class="relative mt-2">
                                            <ComboboxInput class="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:cursor-not-allowed" @change="query = $event.target.value" :display-value="(template) => template?.name" @input="formTouched = true" :disabled="templatesLoading" />
                                            <ComboboxButton class="absolute inset-y-0 right-0 flex items-center px-2 rounded-r-md focus:outline-none">
                                                <svg v-if="templatesLoading" class="w-5 h-5 text-gray-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <ChevronDownIcon v-else class="w-5 h-5 text-gray-400" aria-hidden="true" />
                                            </ComboboxButton>

                                            <ComboboxOptions v-if="filteredTemplate.length > 0" class="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                <ComboboxOption v-for="template in filteredTemplate" :key="template.id" :value="template" as="template" v-slot="{ active, selected }">
                                                    <li :class="['relative cursor-default select-none py-1 pl-3 pr-9', active ? 'bg-indigo-600 text-white' : 'text-gray-900']">
                                                        <span :class="['block truncate', selected && 'font-semibold']">
                                                            {{ template.name }}
                                                        </span>

                                                        <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-indigo-600']">
                                                            <CheckIcon class="w-5 h-5" aria-hidden="true" />
                                                        </span>
                                                    </li>
                                                </ComboboxOption>
                                            </ComboboxOptions>
                                        </div>
                                    </Combobox>

                                    <div class="flex items-center justify-between w-full mt-4">
                                        <div class="flex flex-col flex-1 w-0 text-sm font-medium text-gray-600">
                                            {{ $t("configTemplates.loadPreviousControllerIds") }}
                                        </div>
                                        <input v-model="loadPrevControllerID" type="checkbox" value="" class="text-indigo-600 transition-colors duration-200 ease-in-out scale-150 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600" />
                                    </div>

                                    <div class="flex flex-col w-full mt-4">
                                        <div class="block text-sm font-medium leading-6 text-gray-900"> {{ $t("configTemplates.header.preview") }}</div>
                                        <div class="w-full mt-1 border-gray-200 whitespace-nowrap h-fit">
                                            <div class="flex min-w-full" v-for="(placeholder, index) in placeholders" :key="index">
                                                <div class="w-1/4 px-4 py-2 text-gray-500 bg-gray-100 border whitespace-nowrap"> {{ placeholder.placeholder }} </div>
                                                <input v-model="placeholder.value" :placeholder="placeholder.isNothingFound ? $t('configTemplates.header.noIdFound') : $t('configTemplates.header.example')" :class="[
                                                    'w-full px-4 py-2 border-b-2 border-gray-200 whitespace-nowrap focus:shadow-md focus:bg-gray-50 focus:outline-none focus:ring-0',
                                                    { 'border-red-500': placeholder.showError }
                                                ]" @input="formTouched = true" />
                                            </div>
                                            <div v-if="previewError" class="p-3 mt-2 text-sm text-red-700 whitespace-normal border border-red-200 rounded-md bg-red-50">
                                                {{ $t("configTemplates.applyFailed") }} {{ previewError }}
                                            </div>
                                            <div v-if="previewSection" class="mt-2 text-sm text-gray-500">
                                                {{ $t("configTemplates.mergedInto") }} <span class="font-mono text-gray-700">{{ previewSection }}</span>
                                            </div>
                                            <JsonEditorVue class='w-full mt-2 border-gray-200 rounded-lg whitespace-nowrap' v-model="exampleTemplate" v-bind="{ mode: 'text', mainMenuBar: false, readOnly: true }" />
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        <div class="mt-5 sm:mt-6 sm:mx-4 sm:flex sm:flex-row-reverse">
                            <button type="button" :disabled="disabledButton" class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:ring-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400" @click="confirmOpen = true">
                                {{ $t("common.action.apply") }}
                            </button>
                            <button type="button" class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm" @click="selectedTemplate = {}; $emit('cancel')" ref="cancelButtonRef">
                                {{ $t("common.action.cancel") }}
                            </button>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
    <SimpleAlert @cancel="confirmOpen = false" @approve="save" :open="confirmOpen" :title="$t('alert.applyChangesTitle')" :description="$t('alert.applyChangesDescription')" :action="$t('common.action.apply')" type="info"></SimpleAlert>
</template>

<script>
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot, Combobox, ComboboxButton, ComboboxInput, ComboboxLabel, ComboboxOption, ComboboxOptions } from "@headlessui/vue";
import { CheckIcon, ChevronDownIcon } from "@heroicons/vue/outline";
import { ref, computed, watch } from "vue";
import JsonEditorVue from 'json-editor-vue';
import SimpleAlert from "@/components/modals/SimpleAlert";
import { getTemplatePlaceholders, parseTemplateBody } from "@/lib/configTemplate";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

export default {
    components: {
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        Combobox,
        ComboboxButton,
        ComboboxInput,
        ComboboxLabel,
        ComboboxOption,
        ComboboxOptions,
        CheckIcon,
        ChevronDownIcon,
        JsonEditorVue,
        SimpleAlert
    },
    props: ["open"],
    emits: ["approve", "cancel"],
    setup(props, { emit }) {
        const { t } = useI18n();
        const store = useStore();

        const device = computed(() => store.state.device.current);
        const serialNumber = computed(() => device.value.serialNumber);
        const templatesLoading = computed(() => store.state.device.loadingConfigTemplates);

        watch(() => props.open, async (isOpen) => {
            if (isOpen && device.value.owner?.id && !store.state.device.current.configTemplates?.length) {
                await store.dispatch("device/queryConfigTemplates");
            }
        }, { immediate: true });

        const templates = computed(() =>
            store.state.device.current.configTemplates || []
        );

        const query = ref('');
        const selectedTemplate = ref({});
        const filteredTemplate = computed(() =>
            query.value === ''
                ? templates.value
                : templates.value.filter(template =>
                    template.name.toLowerCase().includes(query.value.toLowerCase())
                )
        );

        const loadPrevControllerID = ref(false);
        const exampleTemplate = ref();
        const placeholders = ref([]);
        const confirmOpen = ref(false);
        const formTouched = ref(false);
        const disabledButton = ref(true);
        const previewError = ref("");
        const previewSection = ref("");

        const checkControllerIdForPlaceholder = (placeholder) => {
            if (!loadPrevControllerID.value) return "";
            const template = JSON.parse(selectedTemplate.value.template || "{}");
            const destinations = template.destinations || template.alarm_app?.destinations;

            const firstControllerId = destinations?.find(d => d?.scaip?.controller_id)?.scaip?.controller_id;

            if (
                firstControllerId !== placeholder ||
                !firstControllerId?.startsWith("$") ||
                !firstControllerId?.endsWith("$")
            ) {
                return "";
            }

            const fullControllerId = device.value.configV2?.alarm_app?.destinations
                ?.find(d => d?.scaip?.controller_id)?.scaip?.controller_id;

            return fullControllerId || "nothingfound";
        };

        const getPlaceholders = () => {
            if (!selectedTemplate.value?.template) return [];

            return getTemplatePlaceholders(selectedTemplate.value.template).map((ph, index) => {
                const val = checkControllerIdForPlaceholder(ph);
                return {
                    placeholder: ph,
                    value: val === "nothingfound" ? "" : val,
                    id: index + 1,
                    isNothingFound: val === "nothingfound",
                    showError: false
                };
            });
        };

        const placeholderValues = () => placeholders.value.reduce((acc, placeholder) => {
            acc[placeholder.placeholder] = placeholder.value;
            return acc;
        }, {});

        let previewTimeout = null;
        const refreshPreview = () => {
            clearTimeout(previewTimeout);
            previewTimeout = setTimeout(async () => {
                if (!selectedTemplate.value?.id || disabledButton.value) {
                    exampleTemplate.value = parseTemplateBody(selectedTemplate.value?.template);
                    previewError.value = "";
                    previewSection.value = "";
                    return;
                }

                const { results } = await store.dispatch("device/applyConfigTemplate", {
                    templateId: selectedTemplate.value.id,
                    devices: [{ deviceId: device.value.id, values: placeholderValues() }],
                    dryRun: true,
                });

                const result = results?.[0];
                previewError.value = result?.ok ? "" : (result?.error?.message || "");
                if (result?.ok) {
                    exampleTemplate.value = JSON.parse(result.renderedTemplate);
                    previewSection.value = result.section;
                }
            }, 400);
        };

        watch(loadPrevControllerID, () => {
            if (selectedTemplate.value?.template) {
                placeholders.value = getPlaceholders();
                formTouched.value = true;
            }
        });

        watch(selectedTemplate, () => {
            placeholders.value = getPlaceholders();
            exampleTemplate.value = parseTemplateBody(selectedTemplate.value.template);
            previewError.value = "";
            previewSection.value = "";
            formTouched.value = false;
            disabledButton.value = true;

            const hasUnfilledRequired = placeholders.value.some(item =>
                !item.isNothingFound && (!item.value || item.value.trim() === '')
            );
            disabledButton.value = hasUnfilledRequired;
        });

        watch(placeholders, () => {
            const anyTouched = placeholders.value.some(item => item.value?.trim().length > 0);
            if (anyTouched) formTouched.value = true;

            let hasUnfilledRequired = false;

            placeholders.value.forEach(item => {
                const isEmpty = !item.value || item.value.trim() === '';
                item.showError = formTouched.value && !item.isNothingFound && isEmpty;
                if (!item.isNothingFound && isEmpty) {
                    hasUnfilledRequired = true;
                }
            });

            disabledButton.value = hasUnfilledRequired;
            refreshPreview();
        }, { deep: true });

        const save = async () => {
            confirmOpen.value = false;

            const { ok, results } = await store.dispatch("device/applyConfigTemplate", {
                templateId: selectedTemplate.value.id,
                devices: [{ deviceId: device.value.id, values: placeholderValues() }],
            });

            if (!ok) {
                previewError.value = results?.[0]?.error?.message || t("notifications.mutations.error");
                return;
            }

            await store.dispatch("device/reloadDevice", serialNumber.value);
            store.dispatch("showNotification", {
                title: t("notifications.mutations.success"),
                text: t("notifications.mutations.delivering"),
                type: "info",
            });

            selectedTemplate.value = {};
            emit("cancel");
        };

        return {
            templates,
            templatesLoading,
            query,
            selectedTemplate,
            filteredTemplate,
            placeholders,
            exampleTemplate,
            confirmOpen,
            loadPrevControllerID,
            formTouched,
            disabledButton,
            previewError,
            previewSection,
            save
        };
    }
};
</script>
